import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowDown, CheckSquare, ChevronRight, CreditCard, FileText, Package, Receipt, Wallet, Wand2, Banknote } from 'lucide-react';
import { useAuth } from '@/stores/auth';
import { arr, money, useBase, useGet, useList } from '@/features/shared/ops/kit';

/** Bursar home: this term's money position, what needs action today, and quick links in the order the job is done. */

function Tile({ to, label, value, hint, tone = 'text-slate-900' }: { to: string; label: string; value: ReactNode; hint?: ReactNode; tone?: string }) {
  return (
    <Link to={to} className="bg-white rounded-2xl border border-slate-200 p-3.5 hover:border-slate-300 block min-w-0">
      <p className="text-xs text-slate-500 truncate">{label}</p>
      <p className={`text-xl sm:text-2xl font-extrabold mt-1 truncate ${tone}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-500 truncate">{hint}</p>}
    </Link>
  );
}

function Panel({ title, to, link, children }: { title: string; to?: string; link?: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2"><h2 className="font-bold text-slate-900">{title}</h2>{to && <Link to={to} className="text-xs font-semibold text-[#0A2540] inline-flex items-center">{link ?? 'Open'}<ChevronRight className="w-3.5 h-3.5" /></Link>}</div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

export function DashboardPage() {
  const base = useBase();
  const name = useAuth((s) => s.user?.name)?.split(' ')[0];
  const dash = useGet<any>('school-dashboard', '/school/dashboard');
  const month = useGet<any>('finance-month', '/school/reports/finance');
  const debtors = useList('debtors', '/school/debtors', { per_page: 5 });
  const overdue = useList('debtors', '/school/debtors', { overdue: 1, per_page: 1 });
  const pendingExp = useList('expenses', '/school/expenses', { status: 'pending_approval' });
  const unmatched = useList('recon-unmatched', '/school/payments', { flag: 'unmatched' });
  const days = useGet<any[]>('recon-days', '/school/reconciliation/days');
  const stock = useGet<any[]>('stock-items', '/school/stock-items', { q: '' });

  const d = dash.data;
  const f = d?.finance;
  const w = d?.wallet;
  const m = month.data;
  const unsigned = arr(days.data).filter((r: any) => !r.reconciled_at && (r.wallet_credits || r.payments_recorded)).length;
  const low = arr(stock.data).filter((s: any) => s.is_active && s.in_stock <= 5).length;
  const todo = [
    { n: unmatched.meta?.total ?? unmatched.rows.length, t: 'payments not linked to a student', to: 'reconciliation', icon: CreditCard },
    { n: unsigned, t: 'days waiting for reconciliation sign-off', to: 'reconciliation', icon: CheckSquare },
    { n: pendingExp.meta?.total ?? pendingExp.rows.length, t: 'expenses waiting for the owner’s approval', to: 'expenses', icon: Wand2 },
    { n: d?.pending?.withdrawals ?? 0, t: 'withdrawals waiting for approval', to: 'withdrawals', icon: ArrowDown },
    { n: low, t: 'store items running low', to: 'stock', icon: Package },
    { n: overdue.meta?.total ?? 0, t: 'students with overdue fees', to: 'debtors', icon: AlertCircle },
  ].filter((x) => x.n > 0);

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{[d?.session?.name, d?.term?.name].filter(Boolean).join(' · ') || 'Bursary'}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Hello{name ? `, ${name}` : ''}</h1>
      </div>

      <div className="rounded-2xl bg-[#0A2540] text-white p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-white/60">This term’s fees</p>
        <div className="flex items-end justify-between gap-3 mt-1">
          <p className="text-3xl font-extrabold">{f ? money(f.collected) : '…'}</p>
          <p className="text-sm text-white/80">{f ? `${f.collection_rate}% collected` : ''}</p>
        </div>
        <div className="h-2 rounded-full bg-white/15 mt-3 overflow-hidden"><div className="h-full bg-green-400" style={{ width: `${Math.min(100, f?.collection_rate ?? 0)}%` }} /></div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
          <div><p className="text-white/60">Billed</p><p className="font-semibold">{f ? money(f.invoiced) : '…'}</p></div>
          <div><p className="text-white/60">Outstanding</p><p className="font-semibold text-amber-300">{f ? money(f.outstanding) : '…'}</p></div>
          <div><p className="text-white/60">Discounts / written off</p><p className="font-semibold">{f ? money(Number(f.discounts) + Number(f.written_off)) : '…'}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile to={`${base}/payments`} label="Collected today" value={f ? money(f.collected_today) : '…'} tone="text-green-700" />
        <Tile to={`${base}/reports`} label="This month" value={m ? money(m.collected) : '…'} hint={m ? `Expenses ${money(m.expenses)} · net ${money(m.net)}` : undefined} />
        <Tile to={`${base}/debtors`} label="Students owing" value={debtors.meta?.total ?? (debtors.isLoading ? '…' : debtors.rows.length)} hint={`${overdue.meta?.total ?? 0} overdue`} tone={(overdue.meta?.total ?? 0) > 0 ? 'text-red-600' : 'text-slate-900'} />
        <Tile to={`${base}/wallet`} label="Wallet balance" value={w ? (w.exists === false ? 'Not set up' : money(w.balance)) : '…'} hint={w?.exists === false ? 'The owner creates the wallet' : undefined} />
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[['record-payment', 'Collect', CreditCard], ['invoices', 'Invoices', FileText], ['receipts', 'Receipts', Receipt], ['expenses', 'Expense', Wand2], ['payroll', 'Payroll', Banknote], ['wallet', 'Wallet', Wallet]].map(([to, l, I]: any) => (
          <Link key={to} to={`${base}/${to}`} className="bg-white rounded-2xl border border-slate-200 py-3 flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300"><I className="w-5 h-5 text-[#0A2540]" />{l}</Link>
        ))}
      </div>

      <Panel title="Needs your attention">
        {!todo.length ? <p className="text-sm text-slate-500">Nothing waiting. Every payment is matched and every day is signed off.</p> : (
          <ul className="divide-y divide-slate-100">
            {todo.map((x) => (
              <li key={x.t}><Link to={`${base}/${x.to}`} className="py-2.5 flex items-center gap-3 text-sm"><x.icon className="w-4 h-4 text-amber-600 shrink-0" /><span className="flex-1"><b>{x.n}</b> {x.t}</span><ChevronRight className="w-4 h-4 text-slate-400" /></Link></li>
            ))}
          </ul>
        )}
      </Panel>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Largest balances" to={`${base}/debtors`} link="All debtors">
          {!debtors.rows.length ? <p className="text-sm text-slate-500">{debtors.isLoading ? 'Loading…' : 'Nobody owes anything.'}</p> : (
            <ul className="divide-y divide-slate-100">{debtors.rows.map((r: any) => (
              <li key={r.student_id ?? r.id ?? r.student} className="py-2 flex items-center gap-3 text-sm"><span className="flex-1 min-w-0 truncate"><b>{r.student}</b> <span className="text-slate-500">· {r.class || '—'}</span></span><span className="font-semibold text-red-600">{money(r.owed)}</span></li>
            ))}</ul>
          )}
        </Panel>
        <Panel title="Recent payments" to={`${base}/payments`} link="All payments">
          {!arr(d?.recent_payments).length ? <p className="text-sm text-slate-500">{dash.isLoading ? 'Loading…' : 'No payments yet.'}</p> : (
            <ul className="divide-y divide-slate-100">{arr(d?.recent_payments).map((r: any) => (
              <li key={r.id} className="py-2 flex items-center gap-3 text-sm"><span className="flex-1 min-w-0"><b className="block truncate">{r.student ?? '—'}</b><span className="text-xs text-slate-500">{r.method?.replace('_', ' ')} · {new Date(r.paid_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span></span><span className="font-semibold text-green-700">{money(r.amount)}</span></li>
            ))}</ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
